import {Injectable} from '@angular/core';
import {
  BudgetOptions,
  BudgetOptionValue,
  BudgetVsActualChartData,
  BudgetVsActualItemForPropertyExpenseCodeResponse,
  BudgetVsActualNode,
  BudgetVsActualResponse,
  BudgetVsActualUnitCapResponse, BudgetVsActualWidgetConfig,
  ChartSortOptions,
  ServiceChargeData,
  SortOrderValue,
  TableColumn
} from '../models/budget-vs-actual-data.model';
import {combineLatest, distinctUntilChanged, distinctUntilKeyChanged, filter, map, Observable, of, switchMap, take, tap} from 'rxjs';
import {BudgetVsActualHttpService} from './budget-vs-actual-http.service';
import {EntityKey, LeaseKey, PropertyKey, UnitKey} from '@feature/pma-entity/pma-entity-types';
import {
  BudgetVsActualLeaseRequest,
  BudgetVsActualRequest,
  BudgetVsActualRequestProperty,
  BudgetVsActualRequestUnit,
  BvaPropertyEntity,
  BvaUnitEntity,
  SaveBudgetVsActualNoteRequest
} from '../models/budget-vs-actual-request.model';
import {ApplicationState} from '@feature/store/application-store';
import {select, Store} from '@ngrx/store';
import * as actions from '../actions/budget-vs-actual.actions';
import * as selectors from '../selectors/budget-vs-actual.selectors';
import * as systemConfigSelectors from '../selectors/budget-vs-actual-config.selectors';
import {getCurrencyCode} from '../selectors/budget-vs-actual.selectors';
import {TreeNode} from 'primeng/api';
import {BudgetVsActualPeriodValue, BudgetVsActualYearValue} from '../models/budget-vs-actual-period.model';
import {EntityKeyService} from '@feature/pma-entity/services/entity-key.service';
import {getCurrencySymbol} from '@angular/common';
import {DataFormatter, WidgetAndConfigId} from "@feature/common";
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ExportCsvService} from '../../shared/export-csv/services/export-csv-data.service';
import * as dashboardWidgetConfigSelectors from '@dashboard-widget/selectors/dashboard-widget-config.selectors';
import * as filterSelectors from "@common/filter/selectors/filter.selectors";
import {DashboardItem} from "@feature/dashboards";

@Injectable()
export class BudgetVsActualFacadeService {
  exportableColumns = [
    {Value: 'Year', Label: 'Year'},
    {Value: 'PeriodLabel', Label: 'Period'},
    {Value: 'PropertyRef', Label: 'Property Ref'},
    {Value: 'PropertyName', Label: 'Property Name'},
    {Value: 'ScheduleRef', Label: 'Schedule Ref'},
    {Value: 'ScheduleName', Label: 'Schedule Name'},
    {Value: 'Category', Label: 'Category'},
    {Value: 'Class', Label: 'Class'},
    {Value: 'ExpenseCode', Label: 'Expense Code'},
    {Value: 'ExpenseDescription', Label: 'Expense Description'},
  ]

  getActualPlusCommitmentOptions(includeCommitments: boolean) {
    const actualPlusCommitmentOptions = [
      {Label: 'Actual', Value: 'Actual', Tooltip: 'The amount invoiced for the period selected.'},
      {Label: 'Prev. Year Actual', Value: 'PreviousYearActual', Tooltip: 'The amount invoiced for the previous year.'},
    ];

    if (includeCommitments) actualPlusCommitmentOptions.push(
      {Label: 'Commitment', Value: 'Commitment', Tooltip: 'Funds Committed for the period selected.'},
      {Label: 'Actual + Comm', Value: 'ActualPlusCommitment', Tooltip: 'Total of invoiced + committed funds.'});

    return actualPlusCommitmentOptions
  }

  getBudgetOptionsForTable(hasBudget2: boolean, hasBudget3: boolean) {
    const budgets = [
      {Label: 'Budget', Value: 'Budget1', Tooltip: 'The amount budgeted for the period selected (budget 1)'}
    ];
    if (hasBudget2) budgets.push({
      Label: 'Budget 2',
      Value: 'Budget2',
      Tooltip: 'The amount budgeted for this period selected (budget 2)'
    });
    if (hasBudget3) budgets.push({
      Label: 'Budget 3',
      Value: 'Budget3',
      Tooltip: 'The amount budgeted for this period selected (budget 3)'
    });
    return budgets;
  }

  varianceOptions(currencyCode: string,
                  hasBudget2: boolean,
                  hasBudget3: boolean,
                  exporting: boolean,
                  includeCommitments: boolean) {
    let symbol = currencyCode;
    if (!exporting)
      symbol = getCurrencySymbol(currencyCode, 'narrow');

    const createOption = (level: number) => {
      return {
        Label: `Variance ${level == 1 ? '' : `${level} `}${symbol ? `(${symbol})` : ""}`,
        Value: `VarianceAmount${level}`,
        Tooltip: includeCommitments
          ? `Variance Amount = (Actual + Any Commitments) - Budget ${level}`
          : `Variance Amount = Actual - Budget ${level}`
      }
    }

    const options = [
      createOption(1)
    ];

    if (hasBudget2)
      options.push(createOption(2));

    if (hasBudget3)
      options.push(createOption(3));

    return options;
  }

  variancePerentOptions(hasBudget2: boolean, hasBudget3: boolean) {
    const variancePercentOptions = [
      {
        Label: 'Variance (%)',
        Value: 'VariancePercent1',
        Tooltip: 'Variance Percentage = Variance amount 1 / Budget 1 x 100'
      },
    ];

    if (hasBudget2) variancePercentOptions.push({
      Label: 'Variance 2 (%)',
      Value: 'VariancePercent2',
      Tooltip: 'Variance Percentage = Variance amount 2 / Budget 2 x 100'
    });
    if (hasBudget3) variancePercentOptions.push({
      Label: 'Variance 3 (%)',
      Value: 'VariancePercent3',
      Tooltip: 'Variance Percentage = Variance amount 3 / Budget 3 x 100'
    });

    return variancePercentOptions;
  }

  apportionmentPercentOptions(hasApporPercent: boolean) {
    return hasApporPercent
      ? [{Label: 'Apportionment (%)', Value: 'ApportionmentPercent', Tooltip: 'The Apportionment Percent for this row'}]
      : [];
  }

  getAllColumns(currencyCode: string,
                hasBudget2: boolean,
                hasBudget3: boolean,
                hasApporPercent: boolean,
                includeExportableColumns: boolean,
                includeCommitments: boolean) {
    const columns = [
      ...this.getBudgetOptionsForTable(hasBudget2, hasBudget3),
      ...this.getActualPlusCommitmentOptions(includeCommitments),
      ...this.varianceOptions(currencyCode, hasBudget2, hasBudget3, includeExportableColumns, includeCommitments),
      ...this.variancePerentOptions(hasBudget2, hasBudget3),
      ...this.apportionmentPercentOptions(hasApporPercent),
    ]

    return includeExportableColumns ? [...this.exportableColumns, ...columns] : columns;
  }

  constructor(
    private http: BudgetVsActualHttpService,
    protected entityKeyService: EntityKeyService,
    protected store: Store<ApplicationState>,
    private readonly dataFormatter: DataFormatter,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly exportCsvService: ExportCsvService) {
  }

  export(widget: DashboardItem) {
    combineLatest([this.allColumns(widget, true)
      .pipe(
        map(columns => [
            ...columns.map(column => {
              return {Label: column.Label, FieldName: column.Value, Type: null}
            })
          ]
        )),
      this.store.pipe(select(selectors.getExportableData(widget.id)))
    ])
      .pipe(take(1))
      .subscribe(([columns, data]) => {
        this.exportCsvService.export(data, columns);
      });
  }

  getPrintData(widgetId: number){
    return combineLatest([
      this.getSelectedColumns(widgetId)
        .pipe(
          map(columns => [
              ...columns.map(column => {
                return column
              })
            ]
          )
        ),
      this.store.pipe(select(selectors.getPrintableData(widgetId))),
      this.store.pipe(select(selectors.getSelectedSchedules(widgetId))),
      this.store.pipe(select(selectors.getAllSchedules(widgetId))),
      this.store.pipe(select(selectors.getPropertyRef(widgetId))),
      this.store.pipe(select(selectors.getPropertyName(widgetId))),
    ]);
  }

  allColumns(widget: DashboardItem, includeExportableColumns: boolean = false): Observable<TableColumn[]> {
    return combineLatest([this.store.pipe(select(selectors.getCurrencyCode(widget.id))),
      this.store.pipe(select(selectors.fieldHasValue(widget.id, 'Budget2'))),
      this.store.pipe(select(selectors.fieldHasValue(widget.id, 'Budget3'))),
      this.store.pipe(select(selectors.fieldHasValue(widget.id, 'ApportionmentPercent'))),
      this.getEffectiveIncludeCommitments(widget)])
      .pipe(map(([currencyCode, hasBudget2, hasBudget3, hasApporPercent, includeCommitments]) =>
        this.getAllColumns(currencyCode, hasBudget2, hasBudget3, hasApporPercent, includeExportableColumns, includeCommitments)));
  }

  userFilter(widget: WidgetAndConfigId) {
    return this.store.pipe(select(filterSelectors.getWidgetFilter(widget.id)));
  }

  getChartSortOptions(widget: DashboardItem): Observable<ChartSortOptions> {

    return combineLatest([
      this.getSelectedBudgetForChart(widget.id),
      this.getEffectiveIncludeCommitments(widget)])
      .pipe(map(([selectedBudget, includeCommitments]) => {
        let sortOptions = {
          PercentageView: [
            {Label: 'Budget Order', Value: {Property: "BudgetOrder", Order: 1, Key: 1}},
            {Label: 'Actual + Commitment (desc)', Value: {Property: "ActualPlusCommitmentPercent", Order: -1, Key: 2}},
            {Label: 'Actual + Commitment (asc)', Value: {Property: "ActualPlusCommitmentPercent", Order: 1, Key: 3}},
            {Label: 'Actual (desc)', Value: {Property: "ActualPercent", Order: -1, Key: 4}},
            {Label: 'Actual (asc)', Value: {Property: "ActualPercent", Order: 1, Key: 5}},
            {Label: 'Funds Commitment (desc)', Value: {Property: "CommitmentPercent", Order: -1, Key: 6}},
            {Label: 'Funds Commitment (asc)', Value: {Property: "CommitmentPercent", Order: 1, Key: 7}},
            {Label: 'Expense Code', Value: {Property: "ExpenseCode", Order: 1, Key: 10}},
            {Label: 'Expense Description', Value: {Property: "ExpenseDescription", Order: 1, Key: 11}}
          ],
          ValueView: [
            {Label: 'Budget Order', Value: {Property: "BudgetOrder", Order: 1, Key: 1}},
            {Label: 'Actual + Commitment (desc)', Value: {Property: "ActualPlusCommitment", Order: -1, Key: 2}},
            {Label: 'Actual + Commitment (asc)', Value: {Property: "ActualPlusCommitment", Order: 1, Key: 3}},
            {Label: 'Actual (desc)', Value: {Property: "Actual", Order: -1, Key: 4}},
            {Label: 'Actual (asc)', Value: {Property: "Actual", Order: 1, Key: 5}},
            {Label: 'Funds Commitment (desc)', Value: {Property: "Commitment", Order: -1, Key: 6}},
            {Label: 'Funds Commitment (asc)', Value: {Property: "Commitment", Order: 1, Key: 7}},
            {Label: 'Budget (desc)', Value: {Property: selectedBudget?.BudgetField ?? 'Budget1', Order: -1, Key: 8}},
            {Label: 'Budget (asc)', Value: {Property: selectedBudget?.BudgetField ?? 'Budget1', Order: 1, Key: 9}},
            {Label: 'Expense Code', Value: {Property: "ExpenseCode", Order: 1, Key: 10}},
            {Label: 'Expense Description', Value: {Property: "ExpenseDescription", Order: 1, Key: 11}}
          ]
        };

        const filterOutCommitmentOptions = !includeCommitments;
        if (filterOutCommitmentOptions) {
          sortOptions.PercentageView = sortOptions.PercentageView.filter(option => !option.Value.Property.includes('Commitment'));
          sortOptions.ValueView = sortOptions.ValueView.filter(option => !option.Value.Property.includes('Commitment'));
        }
        return sortOptions;
      }));
  }

  getSelectedColumns(widgetId: number): Observable<string[]> {
    return this.store.pipe(select(selectors.getSelectedColumns(widgetId))).pipe(filter(x => x != undefined), distinctUntilChanged());
  }

  setSelectedColumns(widgetId: number, columns: string[]) {
    return this.http.setSelectedColumns(widgetId, columns);
  }

  getCurrencyCode(widgetId: number): Observable<string> {
    return this.store.pipe(select(selectors.getCurrencyCode(widgetId)));
  }

  columnsChanged(widgetId: number, columns: string[]) {
    this.store.dispatch(actions.ColumnsChange({widgetId: widgetId, columns: columns}));
  }

  periodChanged(widgetId: number, period: (string | number)[]) {
    this.store.dispatch(actions.PeriodChanged({widgetId: widgetId, period: period}));
  }

  dispatchBudgetDataLoad(
    widgetId: number,
    year: BudgetVsActualYearValue,
    periods: BudgetVsActualPeriodValue[]) {
    this.entityKeyService.getEntityKey().pipe(
      take(1),
      tap(entityKey => {
        this.store.dispatch(actions.BudgetDataLoad({
          widgetId: widgetId,
          entityKey: entityKey,
          propertyTaskUri: year.PropertyTask.Uri,
          budgetPeriodUris: periods.length > 0
            ? periods.map(x => x.BudgetPeriod.Uri)
            : null
        }));
      })).subscribe();
  }

  dispatchSelectedColumnsLoad(widgetId: number) {
    this.store.dispatch(actions.SelectedColumnsLoad({widgetId: widgetId}));
  }

  clearData(widgetId: number) {
    this.store.dispatch(actions.Clear({widgetId: widgetId}));
  }

  selectBudgetVsActualData(widget: WidgetAndConfigId): Observable<TreeNode<BudgetVsActualNode>[]> {
    return this.getEffectiveIncludeCommitments(widget).pipe(
      switchMap((includeCommitments: boolean) =>
        this.store.pipe(select(selectors.getTreeNodeData(widget.id, widget.widgetConfigId, includeCommitments))))
    );
  }

  getSelectedBudgetForChart(widgetId: number): Observable<BudgetOptionValue> {
    return this.store.pipe(select(selectors.getSelectedBudget(widgetId)), distinctUntilKeyChanged('BudgetField'));
  }

  budgetChanged(widgetId: number, budget: BudgetOptionValue) {
    this.store.dispatch(actions.SelectedBudgetChange({widgetId: widgetId, selectedBudget: budget}));
  }

  selectBudgetVsActualChartData(widgetId: number, widgetConfigId: number, searchValue?: string): Observable<BudgetVsActualChartData> {
    return this.store.pipe(select(selectors.getFilteredChartData(widgetId, widgetConfigId, searchValue)));
  }

  getBudgetOptions(widgetId: number): Observable<BudgetOptions[]> {
    return combineLatest([
      this.store.pipe(select(selectors.fieldHasValue(widgetId, 'Budget2'))),
      this.store.pipe(select(selectors.fieldHasValue(widgetId, 'Budget3')))
    ]).pipe(map(([hasBudget2, hasBudget3]) => {
        const options = [{Label: 'Budget 1', Value: {BudgetField: "Budget1", VarianceField: "VarianceAmount1"}}];
        if (hasBudget2)
          options.push({Label: 'Budget 2', Value: {BudgetField: "Budget2", VarianceField: "VarianceAmount2"}});
        if (hasBudget3)
          options.push({Label: 'Budget 3', Value: {BudgetField: "Budget3", VarianceField: "VarianceAmount3"}});
        return options;
      }));
  }

  requestBudgetVsActualData(
    entityKey: EntityKey,
    propertyTaskUri: number,
    budgetPeriodUris: number[],
    widgetId: number): Observable<BudgetVsActualResponse> {
    switch (entityKey.EntityTypeName) {
      case "Lease": {
        const request: BudgetVsActualLeaseRequest =
          this.createLeaseRequest(<LeaseKey>entityKey, propertyTaskUri, budgetPeriodUris, widgetId);
        return this.http.getByPeriod(request, request.LeaseEntity.EntityTypeName);
      }
      case "Unit": {
        const request: BudgetVsActualRequestUnit =
          this.createUnitRequest(<UnitKey>entityKey, propertyTaskUri, budgetPeriodUris, widgetId);
        return this.http.getByPeriod(request, request.PropertyEntity.EntityTypeName);
      }
      default: {
        const request: BudgetVsActualRequestProperty =
          this.getPropertyRequest(entityKey, propertyTaskUri, budgetPeriodUris, widgetId);
        return this.http.getByPeriod(request, request.PropertyEntity.EntityTypeName);
      }
    }
  }

  getPropertyRequest(
    entityKey: EntityKey,
    propertyTaskUri: number,
    budgetPeriodUris: number[],
    widgetId: number): BudgetVsActualRequestProperty {
    let propertyKey = <PropertyKey>entityKey;
    return <BudgetVsActualRequestProperty>{
      Property: entityKey,
      PropertyTaskUri: propertyTaskUri,
      BudgetPeriodUris: budgetPeriodUris,
      WidgetId: widgetId,
      PropertyEntity: <BvaPropertyEntity>{
        Ref: propertyKey.Ref,
        EntityTypeName: propertyKey.EntityTypeName
      }
    }
  }

  createUnitRequest(
    unitKey: UnitKey,
    propertyTaskUri: number,
    budgetPeriodUris: number[],
    widgetId: number): BudgetVsActualRequestUnit {
    return <BudgetVsActualRequestUnit>{
      Property: unitKey,
      PropertyTaskUri: propertyTaskUri,
      BudgetPeriodUris: budgetPeriodUris,
      WidgetId: widgetId,
      PropertyEntity: <BvaUnitEntity>{
        PropertyRef: unitKey.PropertyRef,
        UnitRef: unitKey.UnitRef,
        EntityTypeName: unitKey.EntityTypeName
      }
    }
  }

  getCappedAmountForUnit(
    unitKey: UnitKey,
    propertyTaskUri: number,
    budgetPeriodUris: number[],
    widgetId: number): Observable<BudgetVsActualUnitCapResponse> {
    const unitRequest = this.createUnitRequest(unitKey, propertyTaskUri, budgetPeriodUris, widgetId);
    return this.http.getCappedAmountForUnit(unitRequest);
  }

  getServiceChargeData(
    entityKey: EntityKey,
    propertyTaskUri: number,
    budgetPeriodUris: number[],
    widgetId: number): Observable<ServiceChargeData> {
    switch (entityKey.EntityTypeName) {
      case 'Unit':
        const unitRequest = this.createUnitRequest(<UnitKey>entityKey, propertyTaskUri, budgetPeriodUris, widgetId);
        return this.http.getServiceChargeData(unitRequest, entityKey.EntityTypeName);
      case 'Lease':
        const leaseRequest = this.createLeaseRequest(<LeaseKey>entityKey, propertyTaskUri, budgetPeriodUris, widgetId);
        return this.http.getServiceChargeData(leaseRequest, entityKey.EntityTypeName);
      default:
        return of(undefined);
    }
  }


  createLeaseRequest(
    leaseKey: LeaseKey,
    propertyTaskUri: number,
    budgetPeriodUris: number[],
    widgetId: number): BudgetVsActualLeaseRequest {
    return <BudgetVsActualLeaseRequest>{
      PropertyTaskUri: propertyTaskUri,
      BudgetPeriodUris: budgetPeriodUris,
      WidgetId: widgetId,
      LeaseEntity: leaseKey
    }
  }

  requestBudgetVsActualItemLevelData(
    year: BudgetVsActualYearValue,
    periods: BudgetVsActualPeriodValue[],
    expenseCode: string,
    scheduleRefs: number[],
    apportionment: number,
    widgetId: number
  ): Observable<BudgetVsActualItemForPropertyExpenseCodeResponse> {

    const itemLeveldata = this.http.getItemLevelData({
      BudgetVsActualRequest: <BudgetVsActualRequest>{
        PropertyTaskUri: year.PropertyTask.Uri,
        BudgetPeriodUris: periods.length > 0
          ? periods.map(x => x.BudgetPeriod.Uri)
          : null,
        WidgetId: widgetId
      },
      PropertyExpenseCode: expenseCode,
      ScheduleRefs: scheduleRefs
    });

    const isUnitBvAWidget = this.entityKeyService.getEntityKey().pipe(map(x => x.EntityTypeName == 'Unit' || x.EntityTypeName == 'Lease'));

    return combineLatest(itemLeveldata, isUnitBvAWidget).pipe(map(([response, isUnit]) => {
      response.BudgetVsActualNoteForPropertyExpenseCodeModel.forEach(x => x.CollapseHistory = true);
      response.BudgetVsActualItemForPropertyExpenseCodeModel = response.BudgetVsActualItemForPropertyExpenseCodeModel.map((item) => {
        let expenditure = item.Expenditure;
        if (isUnit)
          expenditure *= apportionment / 100;
        if(item.FromDate !== null && item.ToDate !== null) {
          item.FromAndToDate = `${this.dataFormatter.formatShortDate(item.FromDate)} - ${this.dataFormatter.formatShortDate(item.ToDate)}`;
        }
        item.Expenditure = expenditure;
        return item;
      });
      return response;
    }));
  }

  requestSelectedColumns(widgetId: number): Observable<string[]> {
    return this.http.getSelectedColumns(widgetId);
  }

  saveNote(request: SaveBudgetVsActualNoteRequest) {
    return this.http.saveNote(request);
  }

  scheduleChanged(widgetId: number, scheduleRefs: number[]) {
    this.store.dispatch(actions.ScheduleChange({widgetId: widgetId, scheduleRefs: scheduleRefs}));
  }

  selectedSortOrderChanged(widgetId: number, selectedSortOrder: SortOrderValue) {
    this.store.dispatch(actions.SortOrderChange({widgetId: widgetId, selectedSortOrder: selectedSortOrder}))
  }

  totals(widget: WidgetAndConfigId) {
    return this.getEffectiveIncludeCommitments(widget).pipe(
      switchMap((includeCommitments: boolean) =>
        this.store.pipe(select(selectors.getTotal(widget.id, includeCommitments))))
    );
  }

  getUnitRef(widgetId: number) {
    return this.store.pipe(select(selectors.getUnitRef(widgetId)));
  }

  getPropertyRef(widgetId: number) {
    return this.store.pipe(select(selectors.getPropertyRef(widgetId)));
  }

  schedules(widgetId: number) {
    return this.store.pipe(select(selectors.getAllSchedules(widgetId)));
  }

  itemLevelColumns(widgetId: number, entityTypeName: string): Observable<{
    field: string,
    header: string,
    exportHeader: string
  }[]> {

    return this.store.pipe(select(getCurrencyCode(widgetId))).pipe(map(x => {
        const columns = [
          {field: 'PostingRoutine', header: 'Type', exportHeader: 'Type', displayType: 'plain'},
          {field: 'InvoiceDate', header: 'Invoice Date', exportHeader: 'Invoice Date', displayType: 'date'},
          {field: 'InvoiceRef', header: 'Invoice Ref', exportHeader: 'Invoice Ref', displayType: 'plain'},
          {field: 'Detail', header: 'Narrative', exportHeader: 'Narrative', displayType: 'plain'},
          {field: 'CreditorAccountNo', header: 'Creditor Ref', exportHeader: 'Creditor Reference', displayType: 'plain'},
          {field: 'CreditorAccountName', header: 'Creditor', exportHeader: 'Creditor Name', displayType: 'plain'},
          {field: 'FromAndToDate', header: 'From - To', exportHeader: 'From - To', displayType: 'plain'},
        ]

        let prefix = "";
        if (entityTypeName == 'Unit')
          prefix = "Unit ";

        columns.push({
          field: 'Expenditure',
          header: `${prefix}Expenditure ${x ? `(${getCurrencySymbol(x, 'narrow')})` : ""}`,
          exportHeader: `{prefix}Expenditure ${x ? `(${x})` : ""}`,
          displayType: 'number'
        });

        return columns;
      }
    ));
  }

  itemLevelNoteColumns(): { field: string, header: string }[] {
    return [
      {field: 'Note', header: 'Notes'},
      {field: 'NoteModified', header: 'Date:'},
      {field: 'NoteCreator', header: 'Modified By:'}
    ];
  }

  buildUpdateNoteForm(note: string): UntypedFormGroup {
    return this.formBuilder.group({
      note: [note, [Validators.required, Validators.maxLength(254)]]
    });
  }

  getDashboardWidgetConfig(widget: WidgetAndConfigId) {
    return this.store.pipe(
      select(dashboardWidgetConfigSelectors.getDashboardWidgetConfig(widget.id)),
      filter(d => d != undefined),
      map(c => c.ConfigurationObject as BudgetVsActualWidgetConfig),
      distinctUntilChanged()
    );
  }

  getEffectiveIncludeCommitments(widget: WidgetAndConfigId): Observable<boolean> {
    return combineLatest([
      this.store.pipe(select(selectors.getIncludeCommitments(widget.id))),
      this.store.pipe(select(systemConfigSelectors.getIncludeCommitments(widget.widgetConfigId)))
    ]).pipe(map(([includeCommitments, includeCommitmentsSystemDefault]) => {
        return includeCommitments === undefined || includeCommitments === null
          ? includeCommitmentsSystemDefault
          : includeCommitments;
      }));
  }
}
